import { produce } from 'immer';
import { keys, mergeDeepLeft, omit, reverse } from 'ramda';
import type { Location, NodeEntry, PathRef } from 'slate';
import { Editor, Element, Node, Path, Point, Range, Text, Transforms } from 'slate';
import { v4 as uuid4 } from 'uuid';

import { CHECKBOX_UNCHECKED } from '../../constants';
import { htmlToAline } from '../../serializers/html';
import type { UUID } from '../../types/type-helpers';
import { arrayEntries } from '../../util';
import type {
  AlineContentBlock,
  AlineText,
  ContentNodes,
  FakeElement,
  InsertBookmarkEdit,
  InsertEdit,
  InsertTabEdit,
  InsertTableRowEdit,
  ParagraphElement,
  ParagraphProps,
  SuggestionSource,
  TableElement,
  TextFormatEdit,
  TextProps,
} from '../internal';
import {
  AlineTransforms,
  createDefaultParagraph,
  EditingMode,
  generateLocationId,
  generateSuggestedEditGroupId,
  getIsTextNodeWithComments,
  isAlineDescendant,
  isAlineElement,
  isBookmarkMarkerElement,
  isCheckboxElement,
  isCheckboxOptionFieldElement,
  isContentBlockNodes,
  isDynamicContentElement,
  isInputFieldElement,
  isListItem,
  isListLevel,
  isLocationIdElement,
  isMarkerElement,
  isParagraphElement,
  isParagraphEntry,
  isTabElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  markParagraphAsSuggestedInsert,
  nodeIsSelBoundary,
  Queries,
  SelEventSource,
  transferGlyphPropsForParagraphMerge,
} from '../internal';

export interface PasteContext {
  sourceDocumentId?: UUID;
  targetDocumentId?: UUID;
}

const TextTransforms = {
  /**
   * Cleans content nodes on a detached fake root before insertion, handling paste vs default source, markers,
   * block locationIds, and suggested edits.
   */
  cleanContentNodes<T extends ContentNodes = ContentNodes>(
    editor: Editor,
    nodes: T,
    {
      authorId,
      insertSource,
      isSuggesting,
      pasteContext,
      processEdits,
      source,
      stripCommentIds,
      suggestionGroupId,
    }: {
      authorId: UUID;
      insertSource: 'default' | 'paste';
      isSuggesting: boolean;
      processEdits: boolean;
      stripCommentIds: boolean;
      pasteContext?: PasteContext;
      source?: SuggestionSource;
      suggestionGroupId?: string;
    }
  ): T {
    /**
     * One groupId links a table's inserted rows into a single accept/reject
     * unit. A sourced insertion (e.g. AI chat) reuses the source's groupId for
     * every table so the rows join the same group as the rest of the insertion.
     * An unsourced paste mints a groupId per table: the id is a permanent link
     * (grouping fuses same-id entries regardless of adjacency), so one id
     * shared across tables would make rejecting one pasted table delete the
     * other tables' rows too.
     */
    const fallbackTableRowGroupIds = new Map<Node, string>();
    const fakeNode: FakeElement & { children: T } = {
      children: nodes,
      suggestedEdits: [],
      type: 'fake',
    };
    const fieldDetails =
      insertSource === 'paste'
        ? Queries.fieldDetails(editor, { getDynamic: 'nested', include: 'all' })
        : null;
    const knownFieldIds =
      insertSource === 'paste' && fieldDetails
        ? new Set([...Object.keys(fieldDetails.inChildren), ...Object.keys(fieldDetails.inProps)])
        : null;

    const { children: contentNodes } = produce(fakeNode, draftNode => {
      let nodeListChanged = true;
      const cleanedNodes = new Set<Node>();

      /**
       * Clean all nodes in the editor. If nodes are added or removed during cleaning, restart the
       * process, skipping all nodes that have already been fully cleaned. This is necessary since
       * Node.nodes is not designed to handle node list changes when in non-reversed mode, and in
       * reversed mode it does not return newly added nodes.
       */
      while (nodeListChanged) {
        nodeListChanged = false;

        for (const nodeEntry of Node.nodes(draftNode)) {
          const [node] = nodeEntry;
          if (cleanedNodes.has(node)) continue;

          if (
            handleCheckboxOptionPaste(editor, nodeEntry, draftNode, {
              insertSource,
              knownFieldIds,
              pasteContext,
            }).nodeListChanged
          ) {
            nodeListChanged = true;
            break;
          }

          // Remove dynamic content from the pasted nodes
          if (
            handleDynamicContent(nodeEntry, draftNode, { insertSource, knownFieldIds })
              .nodeListChanged
          ) {
            nodeListChanged = true;
            break;
          }
          if (stripCommentIds && Text.isText(node) && node.comments?.length) node.comments = [];

          // Remove duplicate markers from the pasted nodes
          if (handleMarker(editor, nodeEntry, draftNode).nodeListChanged) {
            nodeListChanged = true;
            break;
          }

          // Set a unique locationId for inserted block elements
          if (isLocationIdElement(node)) node.locationId = generateLocationId();

          addSuggestedEditsToNode(nodeEntry, {
            authorId,
            getTableRowGroupId: () => {
              if (source?.groupId) return source.groupId;
              const [, nodePath] = nodeEntry;
              const table = Node.parent(draftNode, nodePath);
              const existingGroupId = fallbackTableRowGroupIds.get(table);
              if (existingGroupId) return existingGroupId;
              const groupId = generateSuggestedEditGroupId();
              fallbackTableRowGroupIds.set(table, groupId);
              return groupId;
            },
            isSuggesting,
            processEdits,
            source,
            suggestionGroupId,
          });

          cleanedNodes.add(node);
        }
      }
    });

    return contentNodes;
  },
  /**
   * Deletes content in the editor, taking into account editing mode.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-393.
   */
  deleteContent: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      /**
       * Set when the caller passes a range it derived from the live selection (possibly unhung, so
       * no longer identical by value). Lets a grid (table cell rectangle) selection keep its rect
       * semantics instead of being treated as the linear anchor-to-focus range.
       */
      atIsSelection?: boolean;
      direction?: 'forward' | 'backward';
      hanging?: boolean;
      select?: boolean;
      source?: SuggestionSource;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        authorId,
        direction = 'backward',
        editingMode,
        hanging = true,
        select = true,
        source,
        voids = false,
      } = options;
      const isDirectionBackward = direction === 'backward';
      const isDirectionForward = direction === 'forward';
      let { at = editor.selection } = options;

      if (!at) return;

      /**
       * A grid (table cell rectangle) selection clears only the cells of the rectangle. Its linear
       * anchor-to-focus range also covers cells between the rectangle's rows in document order, so it
       * must not be used to enumerate the affected cells. This applies when deleting the live
       * selection, or when a caller passes the selection range by value (atIsSelection, e.g. paste
       * replacing the selection); an explicit unrelated range keeps linear behavior.
       */
      const gridSelectionInfo =
        options.at === undefined || options.atIsSelection === true
          ? Queries.getGridSelectionInfo(editor)
          : Queries.getGridSelectionInfoForLocation(editor, {
              at: Editor.range(editor, options.at),
            });

      // Convert Location to Range for rest of func.
      at = Editor.range(editor, at);

      if (!hanging) at = AlineTransforms.unhangRange(editor, { at });

      // If either of the location's points is within an inline void (e.g., an input field), nudge the selection to the edge of the void(s).
      if (!voids)
        at = AlineTransforms.nudgeRangeToEdges(editor, {
          at,
          match: node =>
            Element.isElement(node) && Editor.isInline(editor, node) && Editor.isVoid(editor, node),
        });

      /**
       * The below occurs regardless of editing mode and, since it actually doesn't delete content, regardless
       * of whether the cursor is at a selection boundary.
       */
      if (Range.isCollapsed(at)) {
        const paragraphEntry = Editor.above<ParagraphElement>(editor, {
          at,
          match: isParagraphElement,
        });
        if (!paragraphEntry) return;

        // Note: Order of events matters in this if block.
        const [paragraph, paragraphPath] = paragraphEntry;
        if (Editor.isStart(editor, at.anchor, paragraphPath)) {
          /**
           * At the start of a list item or indented paragraph, Backspace first
           * un-indents (drops a list level or removes indent) before it can fall
           * through to merging paragraphs.
           */
          if (isDirectionBackward) {
            const activeParaProps = Queries.getActiveParagraphProps(paragraph);
            const hasListOrIndent =
              isListItem(paragraph) ||
              activeParaProps.indentationFirstLine !== 0 ||
              activeParaProps.indentationLeft !== 0;

            if (hasListOrIndent) {
              const maybeUnindentListLevel = activeParaProps.listProps
                ? activeParaProps.listProps.listLevel - 1
                : null;
              const safeNewListLevel = isListLevel(maybeUnindentListLevel)
                ? maybeUnindentListLevel
                : null;
              const newListProps: ParagraphProps['listProps'] | null =
                activeParaProps.listProps && typeof safeNewListLevel === 'number'
                  ? {
                      ...activeParaProps.listProps,
                      listLevel: safeNewListLevel,
                    }
                  : null;
              const safeIndentLeft = activeParaProps.indentationLeft ?? 0;

              AlineTransforms.setParagraphProps(
                editor,
                editingMode,
                authorId,
                {
                  indentationFirstLine: newListProps ? activeParaProps.indentationFirstLine : 0,
                  indentationLeft: newListProps
                    ? Math.max(0, safeIndentLeft - 0.5)
                    : activeParaProps.indentationFirstLine === 0
                      ? 0
                      : activeParaProps.indentationLeft,
                  listProps: newListProps,
                },
                { at: paragraphPath }
              );

              return;
            }
          }

          /**
           * If cursor is at the start of a non-empty paragraph, or a paragraph inside some other
           * block, and prevEntry is a non-paragraph, backspace should do nothing.
           */
          const [parentElement] = Editor.parent(editor, paragraphPath);
          const isTopLevelParagraph = nodeIsSelBoundary(parentElement);
          const canDeleteParagrpah = isTopLevelParagraph && Queries.nodeIsEmpty(paragraph);
          if (isDirectionBackward && !canDeleteParagrpah) {
            const prevEntry = Queries.getPreviousNodeEntry(editor, paragraphPath);
            if (prevEntry && !isParagraphEntry(prevEntry)) return;
          }
        }

        // If cursor is at the end of a paragraph and nextEntry is a non-paragraph, do nothing.
        if (isDirectionForward && Editor.isEnd(editor, at.anchor, paragraphPath)) {
          const nextEntry = Queries.getNextNodeEntry(editor, paragraphPath);
          if (nextEntry && !isParagraphEntry(nextEntry)) return;
        }

        // If cursor is at the start or end of a table cell -> bail so we don't delete the cell.
        const tableCellEntry = Editor.above(editor, { match: isTableCellElement });
        if (tableCellEntry) {
          const [, tableCellPath] = tableCellEntry;
          if (Editor.isStart(editor, at.anchor, tableCellPath) && isDirectionBackward) return;
          if (Editor.isEnd(editor, at.anchor, tableCellPath) && isDirectionForward) return;
        }
      }

      if (!shouldDeleteContent(editor, { at, direction })) return;

      if (editingMode === EditingMode.SUGGESTING) {
        /**
         * Model a collapsed delete at a paragraph boundary as a suggested merge
         * (deleteParagraph on the previous paragraph's glyphProps) rather than a
         * generic content deletion. Matches MS Word's pPr/rPr/del schema.
         */
        if (Range.isCollapsed(at)) {
          const paragraphAtCursor = Editor.above<ParagraphElement>(editor, {
            at,
            match: isParagraphElement,
          });
          if (paragraphAtCursor) {
            const [, paraPath] = paragraphAtCursor;
            const isAtStart = Editor.isStart(editor, at.anchor, paraPath);
            const isAtEnd = Editor.isEnd(editor, at.anchor, paraPath);

            if ((isDirectionBackward && isAtStart) || (isDirectionForward && isAtEnd)) {
              const merged = AlineTransforms.suggestMergeAtCursor(editor, {
                authorId,
                cursorPoint: at.anchor,
                direction: isDirectionBackward ? 'backward' : 'forward',
                editingMode,
                source,
              });
              if (merged) return;
            }
          }
        }

        // Suggest deletion of each grid-selected cell's content individually; the selection stays.
        if (gridSelectionInfo) {
          const gridCellEntries = Queries.getGridSelectionCellEntries(editor) ?? [];
          const cellPathRefs = gridCellEntries.map(([, cellPath]) =>
            Editor.pathRef(editor, cellPath)
          );

          for (const cellPathRef of cellPathRefs) {
            const cellPath = cellPathRef.unref();
            if (!cellPath) continue;

            AlineTransforms.suggestDeletion(editor, {
              at: Editor.range(editor, cellPath),
              authorId,
              editingMode,
              reverse: isDirectionBackward,
              select: false,
              source,
              // Voids true here so we suggest deletion of input fields.
              voids: true,
            });
          }

          return;
        }

        return AlineTransforms.suggestDeletion(editor, {
          at,
          authorId,
          editingMode,
          reverse: isDirectionBackward,
          select,
          source,
          // Voids true here so we suggest deletion of input fields.
          voids: true,
        });
      }

      /**
       * EDITING mode: keep the surviving paragraph's glyph edits correct when a
       * collapsed delete merges two paragraphs (see helper for details).
       */
      if (Range.isCollapsed(at))
        transferGlyphPropsForCollapsedDelete(editor, { at: at.anchor, direction });

      if (Range.isExpanded(at)) {
        const checkboxOptionsRangeRef = Editor.rangeRef(editor, at);
        const { firstRemovedPath, removed } = removeFullySelectedCheckboxOptions(editor, at);

        if (removed) {
          const updatedRange = checkboxOptionsRangeRef.unref();
          if (!updatedRange || Range.isCollapsed(updatedRange)) {
            if (select && firstRemovedPath.length > 0) {
              selectAfterCheckboxOptionRemoval(
                editor,
                Path.parent(firstRemovedPath),
                firstRemovedPath[firstRemovedPath.length - 1]
              );
            }
            return;
          }

          at = updatedRange;
        } else checkboxOptionsRangeRef.unref();
      }

      const [start, end] = Range.edges(at);
      const isDeletionRangeExpanded = Range.isExpanded(at);
      const startTableEntry = Editor.above(editor, { at: start, match: isTableElement });
      const endTableEntry = Editor.above(editor, { at: end, match: isTableElement });

      // If the anchor and focus are both in the same table, delete cell contents individually.
      if (startTableEntry && endTableEntry) {
        const [, startTablePath] = startTableEntry;
        const [, endTablePath] = endTableEntry;

        // Use Path.compare vs. Path.equals to capture tables w/in tables.
        const isSelectionWithinSameTable = Path.compare(startTablePath, endTablePath) === 0;
        if (isSelectionWithinSameTable) {
          // A grid selection clears exactly its rectangle's cells, not the linear range's cells.
          const gridCellEntries = gridSelectionInfo
            ? (Queries.getGridSelectionCellEntries(editor) ?? [])
            : null;
          const cellEntries = gridCellEntries ?? [
            ...Editor.nodes(editor, {
              at,
              match: isTableCellElement,
            }),
          ];
          const commonPath = Path.common(startTablePath, endTablePath);
          // Filtering gives us the cells w/in the table *at the right level of the tree* since tables can be nested in tables.
          const cellPathRefs = cellEntries
            .filter(([, cellPath]) => Path.isDescendant(cellPath, commonPath))
            .map(([cell, cellPath]) => ({
              cell,
              cellPathRef: Editor.pathRef(editor, cellPath),
            }));

          for (const [index, { cell, cellPathRef }] of cellPathRefs.entries()) {
            const isFirstCell = index === 0;
            const cellPath = cellPathRef.unref();
            if (!cellPath) continue;

            const cellRange = Editor.range(editor, cellPath);
            // Grid-selected cells are cleared whole; a linear selection clears only its covered part.
            const cellIntersection = gridCellEntries
              ? cellRange
              : Range.intersection(cellRange, at);
            if (!cellIntersection) continue;

            const isCellIntersectionCollapsed = Range.isCollapsed(cellIntersection);
            if (isDeletionRangeExpanded && isCellIntersectionCollapsed) continue;

            // Use end since the start and end are collapsed during delete and start gets nulled out by Slate during deletion.
            const endRef = Editor.pointRef(editor, Editor.end(editor, cellIntersection));
            const leftOverRange = AlineTransforms.deleteTableContent(editor, {
              cellEntry: [cell, cellPath],
              range: cellIntersection,
            });

            deleteLeftOver(leftOverRange, cellIntersection);

            const endIntersection = endRef.unref();
            /**
             * After clearing a grid selection the cells stay selected (matching Word), so the
             * selection is left in place; a linear delete collapses into the first cell.
             */
            if (select && endIntersection && isFirstCell && !gridCellEntries)
              AlineTransforms.select(editor, endIntersection);
          }

          return;
        }
      }

      const leftOverRange = AlineTransforms.deleteTableContent(editor, { range: at });
      if (!leftOverRange) return;

      const [, endLeftOver] = Range.edges(leftOverRange);
      // Use end since the start and end are collapsed during delete and start gets nulled out by Slate during deletion.
      const endRef = Editor.pointRef(editor, endLeftOver);

      deleteLeftOver(leftOverRange, at);

      const newCursor = endRef.unref();
      if (select && newCursor) AlineTransforms.select(editor, newCursor);

      /** Delete what a table deletion left of the range, if anything. */
      function deleteLeftOver(leftOverRange: Range | null, deletionRange: Range): void {
        if (!leftOverRange) return;
        // A collapsed leftover of an expanded range means the tables took all of it.
        if (Range.isCollapsed(leftOverRange) && Range.isExpanded(deletionRange)) return;

        transferGlyphPropsForExpandedDelete(editor, leftOverRange);

        /**
         * Voids false, in the context of Slate's delete, means that voids will be deleted as a unit and individual
         * characters in a void won't be deleted character-by-character.  Typically this should be false to delete things
         * like page breaks and input fields as a unit vs. character-by-character.
         */
        Transforms.delete(editor, {
          at: leftOverRange,
          hanging,
          reverse: isDirectionBackward,
          voids,
        });
      }
    });
  },

  /**
   * Inserts an array of content nodes at a location.
   */
  insertContentNodes(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      nodes: ContentNodes;
      at?: Location;
      hanging?: boolean;
      insertSource?: 'default' | 'paste';
      merge?: boolean;
      mode?: 'highest' | 'lowest';
      pasteContext?: PasteContext;
      processEdits?: boolean;
      select?: 'start' | 'end' | 'none';
      source?: SuggestionSource;
      stripCommentIds?: boolean;
      suggestionGroupId?: string;
      voids?: boolean;
    }
  ): ContentNodes | null {
    return AlineTransforms.withoutNormalizing(editor, (): ContentNodes | null => {
      const {
        authorId,
        editingMode,
        hanging = false,
        insertSource = 'default',
        merge = true,
        nodes,
        pasteContext,
        // TODO: This should be dependent on `editingMode` AL-3218 https://arity.atlassian.net/browse/AL-3218
        processEdits = true,
        select = 'end',
        source,
        stripCommentIds = false,
        suggestionGroupId,
        voids = false,
      } = options;
      let { at = editor.selection } = options;

      if (!at || !nodes.length) return null;

      const isSuggesting = editingMode === EditingMode.SUGGESTING;
      const cleanNodes = AlineTransforms.cleanContentNodes(editor, nodes, {
        authorId,
        insertSource,
        isSuggesting,
        pasteContext,
        processEdits,
        source,
        stripCommentIds,
        suggestionGroupId,
      });

      const tableEntryAbove = Path.isPath(at)
        ? Node.has(editor, at)
          ? Editor.above(editor, { at, match: isTableElement, mode: 'highest' })
          : undefined
        : Editor.above(editor, { at, match: isTableElement, mode: 'highest' });
      const [firstNode] = cleanNodes;

      /**
       * Special handling for a pasted table
       */
      if (
        insertSource === 'paste' &&
        cleanNodes.length === 1 &&
        isTableElement(firstNode) &&
        tableEntryAbove
      ) {
        handleTableInsertion(editor, {
          at,
          authorId,
          editingMode,
          insertionTable: firstNode,
          selectedTableEntry: tableEntryAbove,
        });
        return cleanNodes;
      }

      if (Range.isRange(at)) {
        const isGridSelection = Boolean(Queries.getGridSelectionInfoForLocation(editor, { at }));
        if (!hanging && !isGridSelection)
          at = AlineTransforms.unhangRange(editor, { at, select: false });

        if (Range.isCollapsed(at)) {
          at = at.anchor;
        } else {
          const [start, end] = Range.edges(at);
          const startRef = Editor.pointRef(editor, start);
          const endRef = Editor.pointRef(editor, end);

          AlineTransforms.deleteContent(editor, { at, authorId, editingMode, select: false });

          const startAfterDelete = startRef.unref();
          const endAfterDelete = endRef.unref();
          // Add endAfterDelete ?? startAfterDelete as sometimes endAfterDelete gets nulled out.
          const point = isSuggesting ? startAfterDelete : (endAfterDelete ?? startAfterDelete);
          if (!point) return null;

          at = point;
        }
      }

      let isSplit = false;
      let isSplitAtStart = false;
      let isSplitAtEnd = false;

      /**
       * Determine what level of the tree to split based on the firstNode being inserted. If the
       * firstNode is a Text node, split at the text level, etc.
       */
      if (Point.isPoint(at)) {
        const [firstNode] = cleanNodes;
        const matcher = constructSplitMatch(editor, firstNode);
        const splitEntries = Editor.nodes(editor, {
          at: at.path,
          match: matcher,
          mode: 'lowest',
          voids,
        });
        const splitEntry = splitEntries.next().value;
        if (!splitEntry) return null;

        const [splitNode, splitPath] = splitEntry;
        const pathRef = Editor.pathRef(editor, splitPath);

        isSplit = true;
        isSplitAtStart = Editor.isStart(editor, at, splitPath);
        isSplitAtEnd = Editor.isEnd(editor, at, splitPath);

        Transforms.splitNodes(editor, {
          at,
          match: matcher,
          mode: 'lowest',
          voids,
        });

        const path = pathRef.unref();
        if (!path) return null;

        /**
         * Path.next if isSplitAtEnd since Transforms.splitNodes doesn't do a split if the location
         * is at an end of the node being split.
         */
        at = isSplitAtEnd ? Path.next(path) : path;

        /**
         * A mid-paragraph split in suggesting mode creates a new pilcrow on the
         * upper half — track it as a suggested paragraph insertion so rejecting the
         * insertion restores the original single paragraph.
         */
        if (isSuggesting && !isSplitAtStart && !isSplitAtEnd && isParagraphElement(splitNode)) {
          markParagraphAsSuggestedInsert(editor, Path.previous(at), {
            authorId,
            groupId: suggestionGroupId,
          });
        }
      }

      /**
       * Whether to merge with the existing blocks before/after the inserted nodes. Generally, we
       * want to merge before if we weren't at the start, and after if we weren't at the end.
       * There's a special case when isSplitAtStart and isSplitAtEnd are both true, in which case we
       * want to merge at the start to simulate replacing the empty paragraph we pasted into.
       */
      const mergeAtStart = isSplit && (!isSplitAtStart || isSplitAtEnd);
      const mergeAtEnd = isSplit && !isSplitAtEnd;

      const maybeParentEntry = Node.has(editor, at) ? Editor.parent(editor, at) : null;
      if (maybeParentEntry && !voids && Editor.void(editor, { at: maybeParentEntry[1] }))
        return null;

      // This should track the proper insert path as we insert nodes.
      const pathRef = Editor.pathRef(editor, at);

      if (isContentBlockNodes(editor, cleanNodes)) {
        /**
         * Paths of inserted paragraphs that land as their own block, so that
         * after all the merges settle we can mark each one as a suggested
         * paragraph insertion (see the loop below). A boundary block that
         * merges into an existing paragraph is left out: a first block folding
         * into the upper split half (already marked by the split above) or a
         * last block absorbing the lower half (which keeps the original
         * paragraph's pilcrow).
         */
        const insertedParagraphRefs: PathRef[] = [];

        for (const [index, node] of cleanNodes.entries()) {
          const processedBlock = processContentBlock(node, { authorId, editingMode });
          const isFirst = index === 0;
          const isLast = index === cleanNodes.length - 1;
          if (!pathRef.current) continue;

          const insertPath = pathRef.current;

          editor.apply({ node: processedBlock, path: insertPath, type: 'insert_node' });

          if (
            isSuggesting &&
            isParagraphElement(processedBlock) &&
            !(isFirst && mergeAtStart) &&
            !(isLast && mergeAtEnd)
          ) {
            insertedParagraphRefs.push(Editor.pathRef(editor, insertPath));
          }

          // Make sure to do the select *before* the merge so we select the right location.
          if (isLast && select !== 'none') {
            const point =
              select === 'start'
                ? Editor.start(editor, insertPath)
                : Editor.end(editor, insertPath);
            AlineTransforms.select(editor, point);
          }

          /**
           * If isFirst and isLast are both true at once, we sometimes want to merge twice, hence
           * the for loop.
           */
          for (const shouldMerge of [mergeAtStart && isFirst, mergeAtEnd && isLast]) {
            if (!shouldMerge) continue;

            // Path to next node might not exist (e.g., if inserting at the end of a document).
            const path = isFirst ? insertPath : pathRef.current;

            if (Node.has(editor, path) && merge) {
              const prevEntry = Queries.getPreviousNodeEntry(editor, path);
              if (!prevEntry) continue;

              const [prevNode] = prevEntry;
              if (!isAlineElement(prevNode) || processedBlock.type !== prevNode.type) continue;

              Transforms.mergeNodes(editor, {
                at: path,
                match: n => isAlineElement(n) && n.type === processedBlock.type,
              });
            }
          }
        }

        /**
         * Mark the surviving standalone inserted paragraphs once the merges are
         * done, so rejecting the suggestion removes each whole block (its
         * pilcrow included) instead of stripping the text and leaving an empty
         * paragraph behind. Threads the same `source`/`groupId` as the text
         * edits so source-scoped rejection picks them up.
         */
        for (const ref of insertedParagraphRefs) {
          const markPath = ref.unref();
          if (
            markPath &&
            Node.has(editor, markPath) &&
            isParagraphElement(Node.get(editor, markPath))
          ) {
            markParagraphAsSuggestedInsert(editor, markPath, {
              authorId,
              groupId: suggestionGroupId,
              source,
            });
          }
        }
      } else {
        for (const [index, node] of cleanNodes.entries()) {
          const isLast = index === cleanNodes.length - 1;
          if (!pathRef.current) continue;

          const insertPath = pathRef.current;

          editor.apply({ node, path: insertPath, type: 'insert_node' });

          if (isLast && select !== 'none')
            AlineTransforms.select(editor, Editor.end(editor, insertPath));
        }
      }

      pathRef.unref();

      return cleanNodes;
    });
  },

  /**
   * Inserts HTML into the editor.
   */
  insertHTML(
    editor: Editor,
    options: {
      authorId: UUID;
      editingMode: EditingMode;
      html: string;
      pasteContext?: PasteContext;
      stripCommentIds?: boolean;
    }
  ): ContentNodes | null {
    return AlineTransforms.withoutNormalizing(editor, () => {
      /**
       * Paste:
       * - `at: selection` so pasted paragraphs borrow spacing from the
       *   paragraph being pasted into (the serializer doesn't infer this on its
       *   own).
       * - `preserveHtmlTextStyles: false` so pasted text adopts the document's
       *   color (and drops unsupported fonts); size/supported fonts that differ
       *   from the destination are kept. Emphasis (bold/italic/underline/
       *   strikethrough) and super/subscript are still kept.
       */
      const { blocks, listDefinitions } = htmlToAline(editor, {
        at: editor.selection ?? undefined,
        html: options.html,
        preserveHtmlTextStyles: false,
      });

      Object.entries(listDefinitions).forEach(([listDefinitionId, listDefinition]) => {
        AlineTransforms.createListDefinition(editor, {
          listDefinition,
          listDefinitionId,
        });
      });

      return AlineTransforms.insertContentNodes(editor, {
        at: editor.selection ?? undefined,
        authorId: options.authorId,
        editingMode: options.editingMode,
        insertSource: 'paste',
        nodes: blocks,
        pasteContext: options.pasteContext,
        select: 'end',
        stripCommentIds: options.stripCommentIds,
      });
    });
  },

  /** Inserts plain text while handling '\n' characters */
  insertPlaintext: (
    editor: Editor,
    options: {
      authorId: UUID;
      editingMode: EditingMode;
      text: string;
      at?: Location;
      /** Modify selection? Defaults to true. */
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, select = true, text } = options;
      if (!at) return;

      const paragraphs = text.split('\n');
      const endAt = Editor.end(editor, at);
      const pointRef = Editor.pointRef(editor, endAt);

      paragraphs.forEach((paragraphText, index) => {
        if (!pointRef.current) return;

        const isFirst = index === 0;
        const isLast = index === paragraphs.length - 1;

        AlineTransforms.insertText(editor, {
          /** First iteration should use the at to overwrite range. Pointref thereafter. */
          at: isFirst ? at : pointRef.current,
          authorId,
          editingMode,
          textToInsert: paragraphText,
          voids: true,
        });

        if (!isLast)
          AlineTransforms.insertBreak(editor, { at: pointRef.current, authorId, editingMode });
      });

      const pointAfterInsert = pointRef.unref();
      if (select && pointAfterInsert) AlineTransforms.select(editor, pointAfterInsert);
    });
  },

  /**
   * Note: This is a fork of Slate's Transforms.insertText function (in the Text interface), EXCEPT that our function takes into account editing mode.
   */
  insertText: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      textToInsert: string;
      at?: Location;
      select?: boolean;
      source?: SuggestionSource;
      voids?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, select = true, source, textToInsert, voids = false } = options;
      let { at = editor.selection } = options;

      if (!at) return;
      if (Path.isPath(at)) at = Editor.range(editor, at);

      const inSuggestingMode = editingMode === EditingMode.SUGGESTING;

      if (Range.isRange(at)) {
        if (Range.isCollapsed(at)) {
          at = at.anchor;
        } else {
          const [start, end] = Range.edges(at);
          if (!voids && Editor.void(editor, { at: end })) return;

          const endRef = Editor.pointRef(editor, end);
          const startRef = Editor.pointRef(editor, start);

          inSuggestingMode
            ? AlineTransforms.deleteContent(editor, { at, authorId, editingMode, voids })
            : AlineTransforms.deleteContent(editor, {
                at,
                authorId,
                direction: 'backward',
                editingMode,
                select: false,
                voids,
              });

          /**
           * Note: Do not remove this if/else block into another structure because, when in editing mode, the
           * Transforms.delete call above nulls out the startRef.  So, for instance, we do not want to return
           * if !startRef.current || !endRef.current as it will cause an early return in normal editing mode.
           */
          const startAfterDelete = startRef.unref();
          const endAfterDelete = endRef.unref();
          const pointAfterDelete = startAfterDelete ?? endAfterDelete;
          if (inSuggestingMode) {
            // In suggesting mode, position the caret after the suggested deletion
            // so deleted text appears before inserted text.
            if (!endAfterDelete) return;
            at = endAfterDelete;
          } else {
            // We know we've done a delete here b/c we're in Editing mode so the startRef/endRef should refer the same point.
            if (!pointAfterDelete) return;
            at = pointAfterDelete;
          }

          if (select) {
            AlineTransforms.setSelection(editor, {
              eventSource: SelEventSource.PROGRAMMATIC,
              props: { anchor: at, focus: at },
            });
          }
        }
      }

      if (!voids && Editor.void(editor, { at })) return;

      const textNode = Node.leaf(editor, at.path);
      const userAlreadySuggestedInsert =
        textNode.suggestedEdits?.some(
          edit => edit.type === 'insertNode' && edit.authorId === authorId
        ) ?? false;

      if (!inSuggestingMode) {
        const { offset, path } = at;

        if (!textNode.suggestedEdits?.length) {
          editor.apply({ offset, path, text: textToInsert, type: 'insert_text' });
          return;
        }

        insertTextNode(editor, {
          at,
          authorId,
          editingMode,
          inSuggestingMode,
          select,
          source,
          textProps: { ...Queries.getActiveTextProps(textNode), comments: [] },
          textToInsert,
          voids,
        });
        return;
      }

      // If the user already suggested an insert, simply add text to the suggested insertion...
      const isAISuggestingEdits = Boolean(source);
      if (userAlreadySuggestedInsert && !isAISuggestingEdits) {
        const { offset, path } = at;
        editor.apply({ offset, path, text: textToInsert, type: 'insert_text' });
        return;
      }

      /**
       * We know we're in suggesting mode and the text doesn't already have a suggested insert.
       * Add an insert edit.
       */
      insertTextNode(editor, {
        at,
        authorId,
        editingMode,
        inSuggestingMode,
        select,
        source,
        textProps: { ...Queries.getActiveTextProps(textNode), comments: [] },
        textToInsert,
        voids,
      });
    });
  },

  /**
   * Inserts text into the editor *at the selection*, taking into account editing mode, editor.marks and other items.
   */
  insertTextAtSelection: (
    editor: Editor,
    editingMode: EditingMode,
    authorId: string,
    textToInsert: string
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { marks } = editor;
      if (!editor.selection) return;

      /**
       * Typing over an active grid (table cell rectangle) selection replaces it: every selected cell
       * is cleared and the text is typed into the anchor cell (matching Word). The linear
       * anchor-to-focus range must not be deleted directly, since it also covers cells outside the
       * rectangle.
       */
      const gridSelectionInfo = Queries.getGridSelectionInfo(editor);
      if (gridSelectionInfo) {
        AlineTransforms.deleteContent(editor, { authorId, editingMode });
        AlineTransforms.announce(editor, 'Selection deleted');

        const [, anchorCellPath] = gridSelectionInfo.anchorCellEntry;
        if (!Editor.hasPath(editor, anchorCellPath)) return;

        const anchorCellStart = Editor.start(editor, anchorCellPath);
        AlineTransforms.setSelection(editor, {
          eventSource: SelEventSource.PROGRAMMATIC,
          props: { anchor: anchorCellStart, focus: anchorCellStart },
        });
      }

      const isCollapsed = Range.isCollapsed(editor.selection);

      /**
       * If the cursor is at the end or beginning of an inline or a text node w/ comments,
       * move it outside of the node before inserting new text.
       */
      if (isCollapsed) {
        const inline = Editor.above(editor, {
          match: n =>
            Element.isElement(n) && Editor.isInline(editor, n) && !isCheckboxOptionFieldElement(n),
          mode: 'highest',
        });
        const leafPath = editor.selection.anchor.path;
        const text = Node.leaf(editor, leafPath);
        const isTextWithComments = getIsTextNodeWithComments(text);

        if (inline || isTextWithComments) {
          const path = inline ? inline[1] : leafPath;

          /**
           * The cursor only escapes within its own paragraph. A paragraph's lone empty commented
           * text (e.g. a commented empty table cell) has no sibling to escape to — both of its
           * edges resolve into the neighbouring block, which would move the typed text into e.g.
           * the next table cell. A fresh uncommented text node is inserted in place instead, so
           * typing there stays uncommented like typing at any other comment edge.
           */
          const paragraphEntry = Editor.above(editor, { at: path, match: isParagraphElement });
          const escapePoint = (point: Point | undefined) =>
            point && paragraphEntry && Path.isDescendant(point.path, paragraphEntry[1])
              ? point
              : null;

          const isAtEnd = Editor.isEnd(editor, editor.selection.anchor, path);
          const isAtStart = !isAtEnd && Editor.isStart(editor, editor.selection.anchor, path);
          const escapedPoint = isAtEnd
            ? escapePoint(Editor.after(editor, path))
            : isAtStart
              ? escapePoint(Editor.before(editor, path))
              : null;

          if (escapedPoint) {
            AlineTransforms.setSelection(editor, {
              eventSource: SelEventSource.PROGRAMMATIC,
              props: {
                anchor: escapedPoint,
                focus: escapedPoint,
              },
            });
          } else if ((isAtEnd || isAtStart) && isTextWithComments && !marks) {
            AlineTransforms.announce(editor, textToInsert);
            insertTextNode(editor, {
              at: editor.selection.anchor,
              authorId,
              editingMode,
              inSuggestingMode: editingMode === EditingMode.SUGGESTING,
              select: true,
              textProps: { ...Queries.getActiveTextProps(text), comments: [] },
              textToInsert,
            });

            return;
          }
        }
      }

      if (!isCollapsed) AlineTransforms.announce(editor, 'Selection deleted');
      AlineTransforms.announce(editor, textToInsert);

      if (!marks)
        return AlineTransforms.insertText(editor, { authorId, editingMode, textToInsert });

      // There should be no marks unless the selection is collapsed, but we do this check anyways to be sure.
      if (!Range.isCollapsed(editor.selection)) return;

      const inSuggestingMode = editingMode === EditingMode.SUGGESTING;

      insertTextNode(editor, {
        at: editor.selection.anchor,
        authorId,
        editingMode,
        inSuggestingMode,
        select: true,
        textProps: marks,
        textToInsert,
      });

      editor.marks = null;
    });
  },

  /**
   * Sets props on text nodes.  Note: Does not yet take into account suggested edits.
   */
  setTextProps(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      textProps: Partial<TextProps>;
      at?: Location;
      voids?: boolean;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, textProps, voids = true } = options;
      if (!at) return;

      // editor.selection can change as part of this function, so perform this check here
      const atIsSelection = at === editor.selection;

      if (editor.selection?.listMarker) {
        const listItemEntry = Editor.above(editor, {
          at: editor.selection.focus.path,
          match: isListItem,
        });
        if (!listItemEntry) return;

        const [listItem] = listItemEntry;
        const activeListProps = Queries.renderedParagraphListProps(listItem);
        if (!activeListProps) return;

        AlineTransforms.setListDefinitionProps(editor, {
          editingMode,
          listDefinitionId: activeListProps.listId,
          props: {
            [activeListProps.listLevel]: { markerFormats: textProps },
          },
        });

        return;
      }

      if (Queries.getGridSelectionInfoForLocation(editor, { at })) {
        const gridCellEntries = Queries.getGridSelectionCellEntries(editor) ?? [];
        for (const [, gridCellPath] of gridCellEntries) {
          AlineTransforms.setTextProps(editor, {
            at: gridCellPath,
            authorId,
            editingMode,
            textProps,
            voids,
          });
        }

        return;
      }

      if (Range.isRange(at)) {
        for (const [listItem, listItemPath] of Editor.nodes(editor, { match: isListItem })) {
          const start = Editor.start(editor, listItemPath);
          const [{ text }] = Editor.leaf(editor, start.path);

          const firstWordLength = text.split(/\s+/)[0].length;
          const firstWordRange = {
            anchor: start,
            focus: { offset: firstWordLength, path: start.path },
          };

          if (Range.isCollapsed(firstWordRange)) continue;

          const intersects = Range.intersection(at, firstWordRange);

          if (!intersects || !Range.equals(intersects, firstWordRange)) continue;

          const activeListProps = Queries.renderedParagraphListProps(listItem);
          if (!activeListProps) continue;

          AlineTransforms.setListDefinitionProps(editor, {
            editingMode,
            listDefinitionId: activeListProps.listId,
            props: {
              [activeListProps.listLevel]: { markerFormats: textProps },
            },
          });
        }
      }

      const voidAbove =
        Range.isRange(at) && Range.isCollapsed(at)
          ? Editor.above(editor, {
              at,
              match: n => Element.isElement(n) && editor.isVoid(n),
              voids: true,
            })
          : undefined;
      const isCollapsedInVoid = !!voidAbove;

      const shouldAddMark = atIsSelection && Range.isCollapsed(at) && !isCollapsedInVoid;

      /**
       * Add props to editor.marks if it's the selection and the selection is collapsed (cursor).
       * The cursor doesn't have a concept of suggestedEdits so we don't need to worry about editing mode.
       */
      if (shouldAddMark) {
        const propKeys = keys(textProps);

        for (const propKey of propKeys) Editor.addMark(editor, propKey, textProps[propKey]);

        return;
      }

      if (editingMode === EditingMode.SUGGESTING) {
        const textFormatEdit: TextFormatEdit = {
          authorId,
          type: 'textFormat',
          value: textProps,
        };

        AlineTransforms.suggestTextFormatEdit(editor, { at, editingMode, textFormatEdit, voids });

        return;
      }

      if (Range.isRange(at) && Range.isCollapsed(at) && !isCollapsedInVoid) return;

      /**
       * The selection is expanded and we are not in suggesting mode.
       * We need to split first so we apply properties only to the text nodes at the provided Location.
       */
      const effectiveAt = voidAbove ? Editor.range(editor, voidAbove[1]) : at;

      const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, {
        at: effectiveAt,
        editingMode,
      });
      if (!rangeAfterSplit) return;

      const textEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: rangeAfterSplit,
          match: Text.isText,
          voids,
        }),
      ];

      for (const [textNode, textPath] of textEntries) {
        AlineTransforms.removeConflictingSuggestions(editor, {
          editingMode,
          entry: [textNode, textPath],
          props: textProps,
        });
        Transforms.setNodes(editor, textProps, { at: textPath, split: true, voids });
      }
    });
  },
};

export default TextTransforms;

/**
 * Utility function for inserting a text node in the transforms above.  It really should NOT be used generally/outside of these transforms.
 */
function insertTextNode(
  editor: Editor,
  options: {
    at: Point;
    authorId: string;
    editingMode: EditingMode;
    inSuggestingMode: boolean;
    select: boolean;
    textProps: TextProps;
    textToInsert: string;
    source?: SuggestionSource;
    voids?: boolean;
  }
): void {
  const {
    at,
    authorId,
    editingMode,
    inSuggestingMode,
    select,
    source,
    textProps,
    textToInsert,
    voids,
  } = options;
  const now = new Date().toISOString();
  const insertEdit: InsertEdit = {
    authorId,
    createdAt: now,
    source,
    type: 'insertNode',
    updatedAt: now,
    value: {},
  };
  const pathRef = Editor.pathRef(editor, at.path);
  const isAtEnd = Editor.isEnd(editor, at, at.path);

  AlineTransforms.splitTextAtEdges(editor, { at, editingMode, voids });

  const path = pathRef.unref();
  if (!path) return;

  const insertPath = isAtEnd ? Path.next(path) : path;
  const node: AlineText = {
    ...textProps,
    suggestedEdits: inSuggestingMode ? [insertEdit] : [],
    text: textToInsert,
  };

  Transforms.insertNodes(editor, node, { at: insertPath, select, voids });
}

/**
 * Utility function for determining whether a delete should be executed.  For instance, a delete shouldn't be executed if the selection
 * is a cursor at the start of a footer as the delete would cross into the main document part.
 */
function shouldDeleteContent(
  editor: Editor,
  options: { at: Range; direction: 'forward' | 'backward' }
): boolean {
  const { at, direction } = options;
  const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor, { at });
  if (!selBoundaryEntry) return false;

  /**
   * If the selection is expanded and it's within a selection boundary, go ahead and delete the content
   * since there is no possibility the delete would cross a selection boundary.
   */
  if (Range.isExpanded(at)) return true;

  const [, selBoundaryPath] = selBoundaryEntry;

  switch (direction) {
    case 'forward': {
      const endSelBoundary = Editor.end(editor, selBoundaryPath);

      return !Point.equals(at.anchor, endSelBoundary);
    }
    case 'backward': {
      const startSelBoundary = Editor.start(editor, selBoundaryPath);

      return !Point.equals(at.anchor, startSelBoundary);
    }
  }
}

function handleCheckboxOptionPaste(
  editor: Editor,
  [node, path]: NodeEntry<Node>,
  draftNode: Node,
  {
    insertSource,
    knownFieldIds,
    pasteContext,
  }: {
    insertSource: 'default' | 'paste';
    knownFieldIds: Set<UUID> | null;
    pasteContext?: PasteContext;
  }
): { nodeListChanged: boolean } {
  if (insertSource !== 'paste') return { nodeListChanged: false };
  if (!isCheckboxOptionFieldElement(node) || !node.id || !node.fieldId) {
    return { nodeListChanged: false };
  }

  const { sourceDocumentId, targetDocumentId } = pasteContext ?? {};
  if (sourceDocumentId && targetDocumentId && sourceDocumentId !== targetDocumentId) {
    const index = path[path.length - 1];
    const parent = Node.parent(draftNode, path);
    parent.children.splice(index, 1, ...node.children);
    return { nodeListChanged: true };
  }

  if (!knownFieldIds?.has(node.fieldId)) return { nodeListChanged: false };

  const optionAlreadyInDocument = Editor.nodes(editor, {
    at: [],
    match: n => isCheckboxOptionFieldElement(n) && n.fieldId === node.fieldId && n.id === node.id,
    voids: true,
  }).next().value;

  if (optionAlreadyInDocument) {
    const newOptionId = uuid4();
    node.id = newOptionId;
    node.suggestedEdits = [];

    for (const child of node.children) {
      if (!isCheckboxElement(child)) continue;

      child.optionId = newOptionId;
      child.suggestedEdits = [];

      const stateTextNode = child.children.at(0);
      if (Text.isText(stateTextNode)) {
        stateTextNode.text = CHECKBOX_UNCHECKED;
        stateTextNode.suggestedEdits = [];
      }
    }
  }

  return { nodeListChanged: false };
}

/**
 * Discards any dynamic content from the pasted nodes.
 */
function handleDynamicContent(
  [node, path]: NodeEntry<Node>,
  draftNode: Node,
  {
    insertSource,
    knownFieldIds,
  }: { insertSource: 'default' | 'paste'; knownFieldIds: Set<UUID> | null }
): { nodeListChanged: boolean } {
  if (insertSource !== 'paste') return { nodeListChanged: false };
  if (
    !isDynamicContentElement(node) &&
    !(isInputFieldElement(node) && knownFieldIds && !knownFieldIds.has(node.fieldId))
  ) {
    return { nodeListChanged: false };
  }

  const index = path[path.length - 1];
  const parent = Node.parent(draftNode, path);

  // Unwrap the DC and splay out its children at the same level as the DC.
  parent.children.splice(index, 1, ...node.children);
  return { nodeListChanged: true };
}

/**
 * Discards any duplicate markers from the pasted nodes.
 */
function handleMarker(
  editor: Editor,
  [node, path]: NodeEntry<Node>,
  draftNode: Node
): { nodeListChanged: boolean } {
  if (!isMarkerElement(node)) return { nodeListChanged: false };

  const key = Queries.getMarkerKey(node);
  const isUnique = !Editor.nodes(editor, {
    at: [],
    match: n => isMarkerElement(n) && Queries.getMarkerKey(n) === key,
  }).next().value;

  if (isUnique) return { nodeListChanged: false };

  const index = path[path.length - 1];
  const parent = Node.parent(draftNode, path);
  parent.children.splice(index, 1);

  return { nodeListChanged: true };
}

/**
 * This method mutates the passed in `node` and adds `suggestedEdits` to it.
 *
 * This is useful for pasted nodes and also useful for when the user inserts a
 * tab.
 */
function addSuggestedEditsToNode(
  [node]: NodeEntry,
  {
    authorId,
    getTableRowGroupId,
    isSuggesting,
    processEdits,
    source,
    suggestionGroupId,
  }: {
    authorId: UUID;
    /** Group id for a row's `insertTableRow` edit — called only for table rows. */
    getTableRowGroupId: () => string;
    isSuggesting: boolean;
    processEdits: boolean;
    source?: SuggestionSource;
    suggestionGroupId?: string;
  }
): void {
  if (!isSuggesting || !isAlineDescendant(node)) return;

  if (processEdits) {
    const nodeProps = omit(['text', 'children'], node);

    if (Array.isArray(node.suggestedEdits)) {
      const newProps = reverse(node.suggestedEdits).reduce((props, suggestedEdit) => {
        return mergeDeepLeft(suggestedEdit.value, props);
      }, nodeProps);

      Object.assign(node, newProps);
    }

    if (!Text.isText(node)) node.suggestedEdits = [];
  }

  if (isTabElement(node)) {
    const insertTab: InsertTabEdit = {
      authorId,
      ...(suggestionGroupId ? { groupId: suggestionGroupId } : {}),
      type: 'insertTab',
      value: {},
    };
    node.suggestedEdits = [insertTab];
  }

  if (isBookmarkMarkerElement(node)) {
    const insertBookmark: InsertBookmarkEdit = {
      authorId,
      ...(suggestionGroupId ? { groupId: suggestionGroupId } : {}),
      type: 'insertBookmark',
      value: {},
    };
    node.suggestedEdits = [insertBookmark];
  }

  /**
   * Mark inserted table rows so rejecting the insertion removes the rows
   * (and table normalization then removes the row-less table) instead of
   * leaving an empty table/rows/cells shell behind once the text-level
   * rejections strip the cell content. Mirrors what
   * `createDefaultTableElement` does for toolbar-inserted tables; rows that
   * already carry edits (e.g. that path, which uses `processEdits: false`)
   * are left untouched.
   */
  if (isTableRowElement(node) && !node.suggestedEdits?.length) {
    const insertTableRow: InsertTableRowEdit = {
      authorId,
      groupId: getTableRowGroupId(),
      type: 'insertTableRow',
      value: {},
      ...(source ? { source } : {}),
    };
    node.suggestedEdits = [insertTableRow];
  }

  if (Text.isText(node)) {
    if (node.text.length === 0) return;
    // Add an insert edit if it's a text node as the user is suggesting an insertion.
    const insertEdit: InsertEdit = {
      authorId,
      ...(suggestionGroupId ? { groupId: suggestionGroupId } : {}),
      type: 'insertNode',
      value: {},
      ...(source ? { source } : {}),
    };

    node.suggestedEdits = [insertEdit];
  }
}

/**
 * Utility function for handling inserting a table (insertionTable) inside another table (selectedTable).
 */
function handleTableInsertion(
  editor: Editor,
  options: {
    at: Location;
    authorId: UUID;
    editingMode: EditingMode;
    insertionTable: TableElement;
    selectedTableEntry: NodeEntry<TableElement>;
  }
): void {
  const { at, authorId, editingMode, insertionTable, selectedTableEntry } = options;
  const atStart = Editor.start(editor, at);
  const [selectedTable, selectedTablePath] = selectedTableEntry;
  const [selStartRowIndex, selStartCellIndex] = Path.relative(atStart.path, selectedTablePath);
  if (!selectedTable.children.at(0)) return;

  /**
   * Map the pasted cells onto the destination by GRID column, not physical child index. In a table
   * with merged cells a row has fewer physical cells than grid columns, so a physical index like
   * `selStartCol + cellIndex` can point past a row's children and Editor.range would throw.
   */
  const selStartCellPath = [...selectedTablePath, selStartRowIndex, selStartCellIndex];
  const selStartCell = Node.get(editor, selStartCellPath);
  if (!isTableCellElement(selStartCell)) return;
  const selStartColumnIndex = Queries.getTableCellStartColumn(editor, [
    selStartCell,
    selStartCellPath,
  ]);

  const numSelectedRows = selectedTable.children.length;
  const numSelectedColumns = Queries.getTableColumnCount(selectedTable);
  const numInsertedRows = insertionTable.children.length - (numSelectedRows - selStartRowIndex);
  const numInsertedColumns =
    Queries.getTableColumnCount(insertionTable) - (numSelectedColumns - selStartColumnIndex);

  if (numInsertedRows > 0)
    AlineTransforms.insertTableRows(editor, {
      at: [...selectedTablePath, numSelectedRows - 1],
      authorId,
      editingMode,
      numInsertedRows,
      rowLocation: 'below',
      select: false,
    });

  if (numInsertedColumns > 0) {
    const tableAfterRows = Node.get(editor, selectedTablePath);
    if (!isTableElement(tableAfterRows)) return;
    const lastRowIndex = tableAfterRows.children.length - 1;
    const lastRow = tableAfterRows.children.at(-1);
    if (!lastRow) return;

    AlineTransforms.insertTableColumns(editor, {
      at: [...selectedTablePath, lastRowIndex, lastRow.children.length - 1],
      authorId,
      columnLocation: 'right',
      editingMode,
      numInsertedCols: numInsertedColumns,
      select: false,
    });
  }

  const finalTable = Node.get(editor, selectedTablePath);
  if (!isTableElement(finalTable)) return;
  const matrix = Queries.getTableGridMatrix([finalTable, selectedTablePath]);

  // A merged destination cell occupies several grid slots; only fill it once.
  const filledCellKeys = new Set<string>();
  let hasSelectedFirst = false;

  for (const [rowIndex, insertionRow] of arrayEntries(insertionTable.children)) {
    let columnOffset = 0;
    for (const insertionCell of insertionRow.children) {
      const targetColumnIndex = selStartColumnIndex + columnOffset;
      columnOffset += Queries.renderedTableCellColumnSpan(insertionCell);

      const slot = matrix[selStartRowIndex + rowIndex]?.[targetColumnIndex];
      if (!slot) continue;

      const [, targetCellPath] = slot.tableCellEntry;
      const targetCellKey = targetCellPath.join(',');
      if (filledCellKeys.has(targetCellKey)) continue;
      filledCellKeys.add(targetCellKey);

      AlineTransforms.insertContentNodes(editor, {
        at: Editor.range(editor, targetCellPath),
        authorId,
        editingMode,
        nodes: insertionCell.children,
        select: 'none',
      });

      if (!hasSelectedFirst) {
        AlineTransforms.select(editor, targetCellPath);
        hasSelectedFirst = true;
      }
    }
  }
}

/**
 * Takes an incoming AlineContentBlock that is about to be inserted in AlineTransforms.insertContentNodes and
 * processes it so it conforms to our expected schema (e.g., each table row has the same num of columns with consistent width).
 */
function processContentBlock<T extends AlineContentBlock>(
  node: T,
  options: { authorId: UUID; editingMode: EditingMode }
): T {
  const { authorId, editingMode } = options;
  if (!isTableElement(node)) return node;

  const [rowWithMaxCols] = [...node.children].sort(
    (rowA, rowB) => rowB.children.length - rowA.children.length
  );
  const insertEdit: InsertEdit = {
    authorId,
    type: 'insertNode',
    value: {},
  };
  const textSuggestedEdits = editingMode === EditingMode.SUGGESTING ? [insertEdit] : [];

  return produce(node, draftTable => {
    for (const row of draftTable.children) {
      for (const [cellIndex, cell] of rowWithMaxCols.children.entries()) {
        const existingCell = row.children[cellIndex];
        if (existingCell) {
          // Normalize the cell width so all columns are consistent.
          existingCell.width = cell.width;
          continue;
        }

        const firstParagraphEntry = Queries.getParagraphInRoot<ParagraphElement>(cell);
        if (!firstParagraphEntry) {
          row.children.push({
            ...cell,
            children: [
              createDefaultParagraph({
                text: '',
                textProps: { suggestedEdits: textSuggestedEdits },
              }),
            ],
          });
          continue;
        }

        const [firstParagraph] = firstParagraphEntry;
        const { children, ...firstParaProps } = firstParagraph;
        const textEntries = Node.texts(firstParagraph) as Generator<
          NodeEntry<AlineText>,
          void,
          undefined
        >;
        const firstTextEntry = textEntries.next().value;
        if (!firstTextEntry) {
          row.children.push({
            ...cell,
            children: [
              createDefaultParagraph({
                paraProps: {
                  ...omit(['listLevel', 'listProps'], firstParaProps),
                  suggestedEdits: [],
                },
                text: '',
                textProps: { suggestedEdits: textSuggestedEdits },
              }),
            ],
          });
          continue;
        }

        const [firstText] = firstTextEntry;
        const textProps = Queries.getActiveTextProps(firstText);
        row.children.push({
          ...cell,
          children: [
            createDefaultParagraph({
              paraProps: {
                ...omit(['listLevel', 'listProps'], firstParaProps),
                suggestedEdits: [],
              },
              text: '',
              textProps: { ...textProps, suggestedEdits: textSuggestedEdits },
            }),
          ],
        });
      }
    }
  });
}

function constructSplitMatch(editor: Editor, firstNode: Node): (n: Node) => boolean {
  if (Text.isText(firstNode)) return n => Text.isText(n);
  if (Element.isElement(firstNode) && editor.isInline(firstNode))
    return n => Text.isText(n) || (Element.isElement(n) && Editor.isInline(editor, n));

  return n => isParagraphElement(n);
}

/**
 * Runs `transferGlyphPropsForParagraphMerge` only when a glyph suggestion is
 * present on either side — ordinary editing-mode merges keep slate's default
 * behavior. Without the transfer, the deleted boundary's glyph edit orphans
 * onto the survivor as a phantom suggested ¶.
 */
function transferGlyphPropsIfSuggested(editor: Editor, upperPath: Path, lowerPath: Path): void {
  const upperNode = Node.get(editor, upperPath);
  const lowerNode = Node.get(editor, lowerPath);
  if (!isParagraphElement(upperNode) || !isParagraphElement(lowerNode)) return;

  const upperHasGlyphEdits = (upperNode.glyphProps?.suggestedEdits ?? []).length > 0;
  const lowerHasGlyphEdits = (lowerNode.glyphProps?.suggestedEdits ?? []).length > 0;
  if (!upperHasGlyphEdits && !lowerHasGlyphEdits) return;

  transferGlyphPropsForParagraphMerge(editor, { lowerPath, upperPath });
}

/**
 * Pilcrow transfer for a collapsed delete at a paragraph boundary (Backspace
 * at a paragraph's start / Delete at its end merges it with a neighbor).
 */
function transferGlyphPropsForCollapsedDelete(
  editor: Editor,
  options: { at: Point; direction: 'forward' | 'backward' }
): void {
  const { at, direction } = options;
  const paragraphEntry = Editor.above<ParagraphElement>(editor, { at, match: isParagraphElement });
  if (!paragraphEntry) return;
  const [, paragraphPath] = paragraphEntry;

  const [upperPath, lowerPath] =
    direction === 'backward' &&
    Editor.isStart(editor, at, paragraphPath) &&
    Path.hasPrevious(paragraphPath)
      ? [Path.previous(paragraphPath), paragraphPath]
      : direction === 'forward' && Editor.isEnd(editor, at, paragraphPath)
        ? [paragraphPath, Path.next(paragraphPath)]
        : [null, null];
  if (!upperPath || !lowerPath || !Node.has(editor, lowerPath)) return;

  transferGlyphPropsIfSuggested(editor, upperPath, lowerPath);
}

/**
 * Pilcrow transfer for an expanded delete whose range crosses a paragraph
 * boundary: slate merges the end paragraph into the start paragraph (or
 * removes the start paragraph when the delete empties it), so the surviving
 * pilcrow is the END paragraph's. Call on the leftover range after table rows
 * are handled, immediately before the raw delete.
 */
function transferGlyphPropsForExpandedDelete(editor: Editor, at: Range): void {
  if (Range.isCollapsed(at)) return;

  const [start, end] = Range.edges(at);
  const upperEntry = Editor.above<ParagraphElement>(editor, {
    at: start,
    match: isParagraphElement,
  });
  const lowerEntry = Editor.above<ParagraphElement>(editor, {
    at: end,
    match: isParagraphElement,
  });
  if (!upperEntry || !lowerEntry) return;
  if (Path.equals(upperEntry[1], lowerEntry[1])) return;

  transferGlyphPropsIfSuggested(editor, upperEntry[1], lowerEntry[1]);
}

function selectAfterCheckboxOptionRemoval(
  editor: Editor,
  parentPath: Path,
  indexInParent: number
): void {
  const point =
    indexInParent > 0
      ? Editor.end(editor, parentPath.concat(indexInParent - 1))
      : Editor.start(editor, parentPath);

  AlineTransforms.select(editor, point);
}

function removeFullySelectedCheckboxOptions(
  editor: Editor,
  at: Range
): { firstRemovedPath: Path; removed: boolean } {
  const toRemove = [
    ...Editor.nodes(editor, {
      at,
      match: isCheckboxOptionFieldElement,
      voids: true,
    }),
  ].filter(([, path]) => {
    const optionRange = Editor.range(editor, path);
    return (
      Range.includes(at, Range.start(optionRange)) && Range.includes(at, Range.end(optionRange))
    );
  });

  if (!toRemove.length) return { firstRemovedPath: [], removed: false };

  const pathsDesc = toRemove.map(([, p]) => p).sort((a, b) => Path.compare(b, a));
  const firstRemoved = pathsDesc[pathsDesc.length - 1];

  for (const p of pathsDesc) {
    Transforms.removeNodes(editor, { at: p, voids: true });
  }

  return { firstRemovedPath: firstRemoved, removed: true };
}
